// services/smsService.ts

import axios from "axios";
import { PaginatedResponse } from "../types/core/PaginatedResponse";
import { MAIN_API_URL } from "../config/api";
import { SendSmsRequest, Sms, SmsRecipient, SmsStats } from "../types/features/Sms";

const apiClient = axios.create({
  baseURL: MAIN_API_URL,
});

apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem("access_token");
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem("access_token");
    }
    return Promise.reject(error);
  }
);

// ============================================
// SERVICE
// ============================================

export const smsService = {
  // Liste des SMS
  async getSmsList(page: number = 1, perPage: number = 20): Promise<PaginatedResponse<Sms>> {
    const response = await apiClient.get<PaginatedResponse<Sms>>("/sms", {
      params: { page, per_page: perPage },
    });
    return response.data;
  },

  // Détail d'un SMS
  async getSmsById(id: string): Promise<Sms | null> {
    const response = await apiClient.get<Sms>(`/sms/${id}`);
    return response.data;
  },

  // Statut d'un SMS
  async getSmsStatus(id: string): Promise<{
    id: string;
    message: string;
    total: number;
    sent: number;
    delivered: number;
    failed: number;
    pending: number;
    recipients: SmsRecipient[];
  }> {
    const response = await apiClient.get(`/sms/${id}/status`);
    return response.data;
  },

  // Envoyer un SMS
  async sendSms(data: SendSmsRequest): Promise<{ message: string; data: Sms }> {
    const response = await apiClient.post("/sms/send", data);
    return response.data;
  },

  // Renvoyer un SMS à un destinataire
  async resendSms(smsId: string, recipientId: string): Promise<{ message: string; data: SmsRecipient }> {
    const response = await apiClient.post(`/sms/${smsId}/resend`, { recipient_id: recipientId });
    return response.data;
  },

  // Statistiques des SMS
  async getStats(): Promise<SmsStats> {
    const response = await apiClient.get<SmsStats>("/sms/stats");
    return response.data;
  },
};
